/**
 * Machines in the predicted view, as the HUD sees them: what stands on a tile and how it is doing,
 * by its role: an extractor drawing from its ground, a crystal charge burning its fuse before it
 * breaks the rock round it (rules 24), or an infuser laying its blocks round it (rules 27). The
 * rules decide; these only read.
 */
import {
  breakable,
  drawnBy,
  extractorGround,
  extractorRate,
  extractorStored,
  isCharge,
  isInfuser,
  MACHINES,
  machineAt,
  MATERIALS,
  nextLayTile,
  RULES,
  type Block,
  type ChargeType,
  type CrewState,
  type ExtractorType,
  type InfuserType,
  type Kind,
  type Resource,
} from '@bramvasterd/terrella-rules';

import { blastTiles } from './reach';

export interface ExtractorInfo {
  role: 'extractor';
  type: ExtractorType;
  level: number;
  owner: string;
  resource: Resource;
  /** Whole units in it now. */
  stored: number;
  storage: number;
  /** Units it takes in per period now: its shares of its ground, at most its intake. */
  rate: number;
  /** Units it takes in per period at most. */
  ingest: number;
  /** Steps from its tile it reaches. */
  range: number;
  /** How many ground tiles it draws from. */
  ground: number;
}

export interface ChargeInfo {
  role: 'charge';
  type: ChargeType;
  /** Always 1: a charge goes off once. */
  level: number;
  owner: string;
  /** When it goes off (server time), its whole fuse, and what is left of it now. */
  done: number;
  fuseMs: number;
  left: number;
  /** The rock it breaks, softest first. */
  breaks: readonly Kind[];
  /** Steps from its tile it reaches: 0 its own tile, 1 the ring round it too. */
  range: number;
  /** How many tiles it breaks as the planet is now, its own among them. */
  tiles: number;
}

export interface InfuserInfo {
  role: 'infuser';
  type: InfuserType;
  /** Always 1: an infuser has no levels yet. */
  level: number;
  owner: string;
  /** The blocks in its hopper, the first laid first, and how many it holds at most. */
  hopper: readonly Block[];
  capacity: number;
  /** The lay under way: the ring tile, the block, when it lands, the whole lay's time and what is left of it now; or null. */
  lay: { tile: number; block: Block; done: number; ms: number; left: number } | null;
  /** Why it lays nothing: its hopper is empty, or no tile of its ring takes a block. */
  idle: 'empty' | 'nowhere' | null;
  /** Where its next block goes: the lay's tile, or where one would start now; null for nowhere. */
  next: number | null;
}

export type MachineInfo = ExtractorInfo | ChargeInfo | InfuserInfo;

/** The rock a charge of `power` breaks, softest first: every breakable material up to its power. */
export function rockFor(power: number): Kind[] {
  return [...MATERIALS]
    .filter(([kind, material]) => breakable(kind) && material.hardness <= power)
    .sort(([, a], [, b]) => a.tier - b.tier)
    .map(([kind]) => kind);
}

/** What stands on `tile`, at server time `now`; null when nothing does. */
export function machineInfo(state: CrewState, tile: number, now: number): MachineInfo | null {
  const machine = machineAt(state, tile);
  if (!machine) return null;
  if (isCharge(machine)) {
    const spec = MACHINES[machine.type];
    return {
      role: 'charge',
      type: machine.type,
      level: machine.level,
      owner: machine.owner,
      done: machine.done,
      fuseMs: spec.fuseMs,
      left: Math.max(0, machine.done - now),
      breaks: rockFor(spec.power),
      range: spec.range,
      tiles: blastTiles(state, tile).length,
    };
  }
  if (isInfuser(machine)) {
    const spec = MACHINES[machine.type];
    const lay = machine.lay
      ? {
          ...machine.lay,
          ms: spec.layMs[machine.lay.block],
          left: Math.max(0, machine.lay.done - now),
        }
      : null;
    const next = lay ? lay.tile : nextLayTile(state, tile);
    return {
      role: 'infuser',
      type: machine.type,
      level: machine.level,
      owner: machine.owner,
      hopper: machine.hopper,
      capacity: spec.hopper,
      lay,
      idle: lay ? null : machine.hopper.length === 0 ? 'empty' : next === null ? 'nowhere' : null,
      next,
    };
  }
  const spec = MACHINES[machine.type];
  const level = spec.levels[machine.level - 1];
  return {
    role: 'extractor',
    type: machine.type,
    level: machine.level,
    owner: machine.owner,
    resource: spec.resource,
    stored: extractorStored(state, tile, now),
    storage: level?.storage ?? 0,
    rate: extractorRate(state, tile) / RULES.parts,
    ingest: level?.ingest ?? 0,
    range: level?.range ?? 0,
    ground: extractorGround(state, tile).length,
  };
}

/** How many extractors draw from `tile`. */
export const drawnFrom = (state: CrewState, tile: number): number => drawnBy(state, tile).length;
